/**
 * CLE-2939 follow-up — the multi-file market price upload.
 *
 * Market price CSVs run to 13–17 MB (~175k rows), and a SuperAdmin uploads a dozen or more at a
 * time. Each file goes to `POST /market-prices/upload` as its own dataset, strictly one after
 * another: the API holds a whole file in memory while it parses and inserts it, so parallel
 * requests would multiply that. A failed file never stops the rest.
 */

/** Per-file cap. Decimal megabytes, so a file Finder or Explorer shows as "25 MB" is accepted. */
export const MAX_MARKET_PRICES_FILE_SIZE_BYTES = 25_000_000;
export const MAX_MARKET_PRICES_FILE_SIZE_LABEL = "25 MB";

const DEFAULT_UPLOAD_ERROR = "Failed to upload market prices";

/** Why this file cannot be uploaded, or null when it can. */
export const validateMarketPricesFile = (file: File): string | null => {
  if (!file.name.toLowerCase().endsWith(".csv")) {
    return `${file.name} is not a CSV file`;
  }
  if (file.size > MAX_MARKET_PRICES_FILE_SIZE_BYTES) {
    return `${file.name} is larger than the ${MAX_MARKET_PRICES_FILE_SIZE_LABEL} maximum`;
  }
  return null;
};

/**
 * The dataset display name for each file. One file keeps the typed name exactly (trimmed);
 * several files are each suffixed with their file name, and a repeated file name gets " (2)",
 * " (3)" ... so no two datasets from one batch share a name.
 */
export const buildDisplayNames = (
  baseName: string,
  files: Pick<File, "name">[]
): string[] => {
  const trimmed = baseName.trim();
  if (files.length <= 1) {
    return files.map(() => trimmed);
  }
  const seen = new Map<string, number>();
  return files.map(({ name }) => {
    const count = (seen.get(name) ?? 0) + 1;
    seen.set(name, count);
    const suffix = count > 1 ? `${name} (${count})` : name;
    return `${trimmed} — ${suffix}`;
  });
};

/** The message to show for a failed upload, from an RTK Query error or a thrown Error. */
export const extractUploadErrorMessage = (error: any): string =>
  error?.data?.message ||
  error?.error?.data?.message ||
  error?.message ||
  DEFAULT_UPLOAD_ERROR;

export type UploadStatus = "pending" | "uploading" | "success" | "failed";

export interface IUploadOutcome {
  status: UploadStatus;
  skippedCount?: number;
  error?: string;
}

export interface IUploadResult<T> extends IUploadOutcome {
  item: T;
}

/**
 * Upload `items` one at a time, in order: the next request starts only once the previous one has
 * resolved or failed. `onUpdate(index, outcome)` fires with "uploading" before each request and
 * with "success" / "failed" after it.
 */
export const runSequentialUploads = async <T>(
  items: T[],
  upload: (item: T, index: number) => Promise<{ skippedCount?: number }>,
  onUpdate: (index: number, outcome: IUploadOutcome) => void
): Promise<IUploadResult<T>[]> => {
  const results: IUploadResult<T>[] = [];
  // Sequential on purpose — see the module comment.
  /* eslint-disable no-await-in-loop, no-restricted-syntax */
  for (const [index, item] of items.entries()) {
    onUpdate(index, { status: "uploading" });
    let outcome: IUploadOutcome;
    try {
      const response = await upload(item, index);
      outcome = {
        status: "success",
        skippedCount: response?.skippedCount ?? 0,
      };
    } catch (error) {
      outcome = { status: "failed", error: extractUploadErrorMessage(error) };
    }
    onUpdate(index, outcome);
    results.push({ item, ...outcome });
  }
  /* eslint-enable no-await-in-loop, no-restricted-syntax */
  return results;
};
